import React, { useEffect, useRef } from "react";
import { Warning, X } from "@phosphor-icons/react";

/**
 * Reusable confirmation modal for destructive actions (Admin deletes).
 * - Traps focus on the destructive button by default (per user preference:
 *   they must explicitly confirm; Cancel is one Tab or Escape away).
 * - Escape closes with `onCancel`.
 * - Backdrop click closes with `onCancel`.
 */
const ConfirmDialog = ({
  open,
  title,
  description,
  confirmLabel = "Delete",
  cancelLabel = "Cancel",
  destructive = true,
  busy = false,
  onConfirm,
  onCancel,
  testId = "confirm-dialog",
}) => {
  const confirmRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onCancel?.();
    };
    document.addEventListener("keydown", onKey);
    // Autofocus the destructive button so keyboard users know the primary
    // action, but they can Tab to Cancel or press Escape at any time.
    setTimeout(() => confirmRef.current?.focus(), 0);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${testId}-title`}
      data-testid={testId}
    >
      <button
        type="button"
        aria-label="Cancel"
        onClick={() => !busy && onCancel?.()}
        className="absolute inset-0 bg-ink-900/50 backdrop-blur-sm"
      />
      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl border border-ink-100">
        <div className="flex items-start gap-3 px-5 pt-5">
          {destructive && (
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
              <Warning size={20} weight="fill" />
            </span>
          )}
          <div className="min-w-0 flex-1">
            <h3
              id={`${testId}-title`}
              className="font-heading text-lg font-medium text-ink-900 leading-snug"
            >
              {title}
            </h3>
            {description && (
              <p className="mt-2 text-sm leading-relaxed text-ink-700">
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => !busy && onCancel?.()}
            aria-label="Close"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-ink-400 hover:bg-ink-100 hover:text-ink-700"
          >
            <X size={16} weight="bold" />
          </button>
        </div>
        <div className="mt-5 flex flex-wrap items-center justify-end gap-2 border-t border-ink-100 bg-ink-50/50 px-5 py-3 rounded-b-2xl">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            data-testid={`${testId}-cancel`}
            className="inline-flex items-center rounded-full border border-ink-200 bg-white px-4 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-50 disabled:opacity-60"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            disabled={busy}
            data-testid={`${testId}-confirm`}
            className={`inline-flex items-center rounded-full px-4 py-2 text-sm font-semibold text-white disabled:opacity-60 ${
              destructive
                ? "bg-red-600 hover:bg-red-700"
                : "bg-primary hover:bg-primary-dark"
            }`}
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
